import { Chip, TableCell, TableRow, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ContributingSections from "./ContributingSections";
import EmployeeCostSectionAmountCell from "./EmployeeCostSectionAmountCell";
import EmployeeCostSectionRateCell from "./EmployeeCostSectionRateCell";
import { LegislationSocialSecurityCostSectionRef } from "../../../../models/legislation-social-security-cost/legislation-social-security-cost-section-ref";
import { ProformaEmployeeCostReportSectionRef } from "../../../../models/proforma-employee-cost-report/proforma-employee-cost-report-section-ref";
import { EMPLOYEE_COST_SECTION_TYPES } from "../../../../utils/constants/employee-cost-report-constants";
import { ProformaEmployeeCostReportContext } from "../ProformaEmployeeCostReport";
interface Props {
  section: ProformaEmployeeCostReportSectionRef;
  handleUpdateSection: (updatedSection: ProformaEmployeeCostReportSectionRef) => void;
  contributingSections: ProformaEmployeeCostReportSectionRef[];
  proportionalToSection: ProformaEmployeeCostReportSectionRef | null;
  socialSecuritySection: LegislationSocialSecurityCostSectionRef | null;
}

export interface RateInterval {
  from: number;
  to: number;
  rate: number;
  rawAmount: number; //without min and cap
  amount: number;
  capAmount: number;
  minAmount: number;
  fixedAmount: number;
}

interface State {
  rateIntervals: RateInterval[];
}

const EmployeeCostSection = ({
  section,
  handleUpdateSection,
  contributingSections,
  proportionalToSection,
  socialSecuritySection,
}: Props) => {
  const theme = useTheme();

  const { originalReport, applicableSocialSecurityConfigs, grossSalary } = useContext(
    ProformaEmployeeCostReportContext
  );

  const isNewSection = useMemo(() => {
    return originalReport && !originalReport?.sections.find((repSection) => repSection.id === section.id);
  }, [originalReport?.sections, section.id]);

  const [sSecurityRateAndAmount, setSSecurityRateAndAmount] = useState<State>({
    rateIntervals: [],
  });

  //#region recalculate ssc
  const recalculateSSSectionAmounts = useCallback(() => {
    if (
      section.type?.type?.isSocialSecurityRelevant &&
      section.type?.considerIncomeBrackets &&
      proportionalToSection?.type?.type?.id === EMPLOYEE_COST_SECTION_TYPES.Gross_salary.id
    ) {
      let totalSocialSecurityAmount = 0;
      const rateIntervals: RateInterval[] = [];

      for (let i = 0; i < applicableSocialSecurityConfigs.length; i++) {
        const currentIntervalSSSection: LegislationSocialSecurityCostSectionRef = applicableSocialSecurityConfigs[
          i
        ].socialSecuritySections.find((ssSection) => ssSection.section.id === section.type?.id);

        if (
          grossSalary > applicableSocialSecurityConfigs[i].fromGrossSalary ||
          !applicableSocialSecurityConfigs[i].fromGrossSalary
        ) {
          const previousIntervalCap = i > 0 ? applicableSocialSecurityConfigs[i - 1].toGrossSalary : null;

          const currentIntervalApplicableAmount =
            grossSalary > applicableSocialSecurityConfigs[i].toGrossSalary
              ? grossSalary > applicableSocialSecurityConfigs[i].toGrossSalary &&
                applicableSocialSecurityConfigs[i].toGrossSalary > 0
                ? applicableSocialSecurityConfigs[i].toGrossSalary - applicableSocialSecurityConfigs[i].fromGrossSalary
                : grossSalary - (previousIntervalCap ?? 0)
              : grossSalary - applicableSocialSecurityConfigs[i].fromGrossSalary;

          if (currentIntervalSSSection) {
            const calculatedAmount =
              currentIntervalSSSection.fixedAmount > 0
                ? currentIntervalSSSection.fixedAmount
                : (((currentIntervalApplicableAmount / 100) *
                    (currentIntervalSSSection.rate > 0 ? currentIntervalSSSection.rate : 1)) /
                    (currentIntervalSSSection.dividedBy > 0 ? currentIntervalSSSection.dividedBy : 1)) *
                  (currentIntervalSSSection.multipliedBy > 0 ? currentIntervalSSSection.multipliedBy : 1);

            //Considering min and cap for income bracket interval
            const considerBracketMinAmount =
              currentIntervalSSSection.minAmount > 0 && calculatedAmount < currentIntervalSSSection.minAmount
                ? currentIntervalSSSection.minAmount
                : calculatedAmount;

            const considerBracketMinAndCapAmount =
              currentIntervalSSSection.capAmount > 0 && considerBracketMinAmount > currentIntervalSSSection.capAmount
                ? currentIntervalSSSection.capAmount
                : considerBracketMinAmount;

            //Calculate the sum of income brackets intervals
            //At this level we do not consider the legislation section config(upper level) min and cap amount
            const ssAmountNotAdjustedToSectionTypeIntervals =
              totalSocialSecurityAmount + considerBracketMinAndCapAmount;

            //After calculating the sum of income intervals, we adjust to the legislation section config min and cap amount
            const amountAdjustedToSectionTypeMin =
              section.type.minAmount > 0
                ? ssAmountNotAdjustedToSectionTypeIntervals < section.type.minAmount
                  ? section.type.minAmount
                  : ssAmountNotAdjustedToSectionTypeIntervals
                : ssAmountNotAdjustedToSectionTypeIntervals;

            const amountAdjustedToSectionTypeMinAndCapAmount =
              section.type.capAmount > 0
                ? amountAdjustedToSectionTypeMin > section.type.capAmount
                  ? section.type.capAmount
                  : amountAdjustedToSectionTypeMin
                : amountAdjustedToSectionTypeMin;

            //At this level we update the total amount of social security considering the adjustment on the legislation section config min and cap amount
            totalSocialSecurityAmount = amountAdjustedToSectionTypeMinAndCapAmount;

            rateIntervals.push({
              rate: currentIntervalSSSection.rate,
              from: applicableSocialSecurityConfigs[i].fromGrossSalary,
              to: applicableSocialSecurityConfigs[i].toGrossSalary,
              rawAmount: calculatedAmount,
              amount: considerBracketMinAndCapAmount,
              capAmount: currentIntervalSSSection.capAmount,
              minAmount: currentIntervalSSSection.minAmount,
              fixedAmount: currentIntervalSSSection.fixedAmount,
            });
          }
          handleUpdateSection({ ...section, amount: totalSocialSecurityAmount });

          setSSecurityRateAndAmount({
            rateIntervals,
          });
        }
      }
    }
  }, [
    applicableSocialSecurityConfigs,
    section,
    grossSalary,
    handleUpdateSection,
    proportionalToSection?.type?.type?.id,
  ]);

  useEffect(() => {
    if (section.type?.type?.isSocialSecurityRelevant && section.type.considerIncomeBrackets) {
      recalculateSSSectionAmounts();
    }
  }, [grossSalary, section.type]);
  //#endregion

  return (
    <>
      <TableRow
        style={{
          backgroundColor: section.type?.legislationEmployeeCostSectionGroup?.highlightBackground
            ? theme.palette.background.paper
            : "none",
        }}
      >
        <TableCell
          style={{
            position: "relative",
            fontWeight: section.type?.isBold ? "bold" : "normal",
            color: isNewSection ? theme.palette.primary.main : theme.palette.text.primary,
          }}
        >
          {section.type?.contributesTo && section.type?.contributesTo.length > 0 ? (
            <ContributingSections
              sectionName={section.type?.name ?? ""}
              contributesTo={section.type?.contributesTo ?? []}
              bold={section.type?.isBold}
            />
          ) : (
            section.type?.name
          )}
          {isNewSection && (
            <Tooltip
              title={
                "This sections has been added after report saved. Please save the report again to include it in the new version"
              }
            >
              <Chip
                style={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  fontSize: theme.typography.body2.fontSize,
                }}
                size="small"
                label={"NEW"}
              />
            </Tooltip>
          )}
        </TableCell>
        <EmployeeCostSectionRateCell
          proportionalTo={proportionalToSection}
          currentSection={section}
          socialSecuritySection={socialSecuritySection}
          handleUpdateSection={handleUpdateSection}
          {...(section.type?.type?.isSocialSecurityRelevant &&
            section.type?.considerIncomeBrackets && {
              socialSecurityRates: sSecurityRateAndAmount.rateIntervals,
            })}
        />
        <EmployeeCostSectionAmountCell
          proportionalTo={proportionalToSection}
          currentSection={section}
          handleUpdateSection={handleUpdateSection}
          contributingSections={contributingSections}
          socialSecuritySection={socialSecuritySection}
        />
      </TableRow>
    </>
  );
};
export default EmployeeCostSection;
